import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MessageCircle, MessageSquare, Heart, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "방금 전";
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}일 전`;
  return new Date(iso).toLocaleDateString("ko-KR");
}

function iconFor(type) {
  if (type === "reply") return <MessageSquare className="w-4 h-4 text-blue-500" />;
  if (type === "like") return <Heart className="w-4 h-4 text-rose-500" />;
  return <MessageCircle className="w-4 h-4 text-burgundy-500" />;
}

function messageFor(n) {
  const name = n.actor_name || "회원";
  if (n.type === "reply") return `${name} 님이 회원님의 댓글에 답글을 남겼습니다.`;
  if (n.type === "like") return `${name} 님이 회원님의 글에 좋아요를 눌렀습니다.`;
  return `${name} 님이 회원님의 글에 댓글을 남겼습니다.`;
}

// 알림 아래 인용 문구: 좋아요는 어떤 글인지(제목/첫 문장)를, 댓글/답글은 댓글 내용을 노출
function previewFor(n) {
  if (n.type === "like") return (n.post_title || "").trim();
  return (n.content_preview || "").trim();
}

export default function CommentNotificationsPage() {
  const { token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    fetch(`${API_URL}/api/notifications?limit=100&category=activity`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (authLoading) return;
    if (!token) { navigate("/"); return; }
    load();
  }, [token, authLoading]); // eslint-disable-line

  const markAllRead = async () => {
    await fetch(`${API_URL}/api/notifications/read-all?category=activity`, {
      method: "PUT", headers: { Authorization: `Bearer ${token}` },
    });
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const handleClick = async (n) => {
    if (!n.is_read) {
      await fetch(`${API_URL}/api/notifications/${n.id}/read`, {
        method: "PUT", headers: { Authorization: `Bearer ${token}` },
      });
    }
    if (n.post_id) navigate(`/community/post/${n.post_id}`);
  };

  const unread = items.filter((n) => !n.is_read).length;

  return (
    <div className="min-h-screen bg-[#F4F2EE] pt-24 pb-24" data-testid="comment-notifications-page">
      <div className="max-w-2xl mx-auto px-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-stone-500 hover:text-burgundy-700 mb-5 text-sm transition-colors">
          <ArrowLeft className="w-4 h-4" /> 뒤로 가기
        </button>

        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-burgundy-50 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-burgundy-600" />
            </div>
            <div>
              <h1 className="font-serif text-2xl text-stone-900">댓글 알림</h1>
              <p className="text-sm text-stone-500">내 글에 달린 댓글·답글·좋아요 알림</p>
            </div>
          </div>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-sm text-burgundy-600 hover:text-burgundy-700 flex items-center gap-1" data-testid="comment-notif-mark-all">
              <Check className="w-4 h-4" /> 모두 읽음
            </button>
          )}
        </div>

        <div className="mt-6 bg-white rounded-2xl border border-stone-100 divide-y divide-stone-50 overflow-hidden">
          {loading ? (
            <div className="py-20 text-center text-stone-400 text-sm">불러오는 중…</div>
          ) : items.length === 0 ? (
            <div className="py-20 text-center" data-testid="comment-notif-empty">
              <MessageCircle className="w-9 h-9 mx-auto text-stone-300 mb-2" />
              <p className="text-stone-600">아직 내 글에 달린 댓글 알림이 없어요.</p>
              <p className="text-sm text-stone-400 mt-2">따뜻한 이야기를 나누다 보면 댓글이 쌓일 거예요 🌿</p>
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={`w-full flex items-start gap-3 px-4 py-3.5 hover:bg-stone-50 transition-colors text-left ${!n.is_read ? "bg-burgundy-50/40" : ""}`}
                data-testid={`comment-notif-${n.id}`}
              >
                <div className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center shrink-0">
                  {iconFor(n.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] text-stone-800 leading-snug">{messageFor(n)}</p>
                  {previewFor(n) && <p className="text-[13px] text-stone-500 mt-0.5 truncate">"{previewFor(n)}"</p>}
                  <p className="text-[11px] text-stone-400 mt-1">{timeAgo(n.created_at)}</p>
                </div>
                {!n.is_read && <span className="w-2 h-2 rounded-full bg-red-500 shrink-0 mt-2" />}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
